import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { untypedIsEnabled } from '@ip-only/client-features';

import { Feature, FeatureCollection, Point } from 'geojson';
import {
  DivIcon,
  GeoJSON,
  Icon,
  LatLng,
  LeafletMouseEvent,
  Marker,
  PointExpression,
  circleMarker,
  geoJSON,
} from 'leaflet';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GeoJsonStorageService } from '../../../../shared/geo-json-storage-service/geo-json-storage.service';
import { RolloutProgressReportEntities } from '../../../../shared/self-inspection-reports/service/entities';
import { RolloutProgressReportsService } from '../../../../shared/self-inspection-reports/service/reports/rollout-progress-reports.service';
import { mapStatusForIcon } from '../../../../shared/util/report-status-icon-helper';
import { ResourcesService } from '../../../../user/resources.service';
import {
  DistPointLayerTypeMap,
  DistributionPointProperties,
  IconPaths,
  InfoPointLayerTypeMap,
  InfoPointProperties,
  InformationPointResponse,
  PointLayerTypes,
  PointType,
  PopupTypes,
  ReportStatus,
  iconTypeSizes,
} from '../../../span-progress-entities';
import {
  PointStyles,
  pointInfo,
  reportStatusColors,
} from '../../../span-progress-view/map-view-constants';
import { MapApiService } from '../../api/map-api.service';
import { AbstractLayerService } from '../abstract-layer.service';
import { KpReportStatusService } from '../../kp-report-status.service';
import { createClusterIconHtml } from './cluster-entities';
import { ClusterPointService } from './cluster-point.service';

interface PointLayerMap {
  [key: string]: GeoJSON<PointProperties>;
}

type PointProperties = DistributionPointProperties | InfoPointProperties;

export type PointCollection = FeatureCollection<GeoJSON.Point, PointProperties>;

@Injectable({ providedIn: 'root' })
export class PointService extends AbstractLayerService<PointLayerMap> {
  private readonly apiService = inject(MapApiService);
  private readonly clusterPointService = inject(ClusterPointService);
  private readonly v3ReportsService = inject(RolloutProgressReportsService);
  private readonly kpReportStatusService = inject(KpReportStatusService);
  private readonly geoJsonStorageService = inject(GeoJsonStorageService);
  private readonly resourceService = inject(ResourcesService);

  private cancelSub$ = new Subject<void>();

  showMultiPoint = untypedIsEnabled('multiPoint');

  private readonly _storedIfs = signal<string>('');

  constructor() {
    super({});
  }
  loadData = this.loadPointData;

  storedV3Reports = this.v3ReportsService.storedV3Reports;

  loadPointData(ifs: string) {
    this.cancelSub$.next();
    this.setLoading();
    combineLatest([
      this.apiService.getDistributionPointGeoJson(ifs),
      this.apiService.getInformationPoints(ifs),
    ])
      .pipe(takeUntil(this.cancelSub$))
      .subscribe({
        next: ([distPointResponse, infoPointResponse]) => {
          this.geoJsonStorageService.setPoints(ifs, distPointResponse);
          this.showMultiPoint &&
            this.clusterPointService.createMarkerCluster(
              distPointResponse.features,
            );

          if (!this.loadingState$.getValue().hasLoaded) {
            this.showMultiPoint
              ? this.initDistPoints(
                  distPointResponse.features,
                  DistPointLayerTypeMap,
                )
              : this.initPointLayers(
                  distPointResponse.features,
                  DistPointLayerTypeMap,
                );

            const features = infoPointResponse.map((infoPoint) =>
              this.createFeature(infoPoint),
            );

            const featureCollection = this.createFeatureCollection(features);

            this.initPointLayers(
              featureCollection.features,
              InfoPointLayerTypeMap,
            );
            this.setLoaded();
          }
        },
        error: (err: HttpErrorResponse) => this.handleError(err),
      });
  }

  private createFeature(infoPoint: InformationPointResponse) {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [Number(infoPoint.longitude), Number(infoPoint.latitude)],
      },
      properties: {
        name: infoPoint.name,
        description: infoPoint.desc,
        markerTypeId: infoPoint.markerTypeId,
        freeText: infoPoint.freeText,
      },
    } as Feature<GeoJSON.Point, InfoPointProperties>;
  }

  private createFeatureCollection(infoPoint: Feature<GeoJSON.Point>[]) {
    return {
      type: 'FeatureCollection',
      features: infoPoint,
    } as FeatureCollection<GeoJSON.Point, InfoPointProperties>;
  }

  private initDistPoints(
    features: Feature<Point, DistributionPointProperties>[],
    layerTypeMap: { [key: string]: number[] },
  ) {
    const featuresWithMultiPoints =
      this.clusterPointService.initDistPoints(features);

    this.initPointLayers(
      featuresWithMultiPoints as Feature<GeoJSON.Point, PointProperties>[],
      layerTypeMap,
    );
  }
  private initPointLayers(
    features: Feature<GeoJSON.Point, PointProperties>[],
    layerTypeMap: { [key: string]: number[] },
  ) {
    const collections = features.reduce(
      (
        accResult: {
          [key in string]: PointCollection;
        },
        feature,
      ) => {
        const layerType = this.getLayerType(
          feature.properties.markerTypeId,
          layerTypeMap,
        );

        if (!layerType) {
          return accResult;
        }
        const currFeatures = accResult[layerType]
          ? accResult[layerType].features
          : [];
        return {
          ...accResult,
          [layerType]: {
            type: 'FeatureCollection',
            features: [...currFeatures, feature],
          },
        };
      },
      {},
    );
    Object.keys(collections).forEach((key) =>
      this.initPointLayer(
        collections[key] as PointCollection,
        key as PointLayerTypes,
      ),
    );
  }

  private getLayerType(id: number, layerTypeMap: { [key: string]: number[] }) {
    return Object.keys(layerTypeMap).find((type: string) =>
      layerTypeMap[type].includes(id),
    ) as PointLayerTypes;
  }

  private initPointLayer(geoJson: PointCollection, typeId: PointLayerTypes) {
    this.clearLayerDataIfStoredIfsChanged();

    const layer = geoJSON<PointProperties>(geoJson, {
      pointToLayer: (feature: GeoJSON.Feature, latLng: LatLng) => {
        switch (feature.properties?.markerTypeId) {
          case PointType.Cabinet:
            return this.setIcon(
              latLng,
              this.getImagePathForSinglePoint(
                feature.properties as DistributionPointProperties,
                'CABINET',
              ),
              'default',
            );
          case PointType.Site:
            return this.setIcon(
              latLng,
              this.getImagePathForSinglePoint(
                feature.properties as DistributionPointProperties,
                'SITE',
              ),
              'default',
            );
          case PointType.Hub:
            return this.setIcon(
              latLng,
              this.getImagePathForSinglePoint(
                feature.properties as DistributionPointProperties,
                'HUB',
              ),
              'hub',
            );
          case PointType.Hp:
            return this.setIcon(
              latLng,
              this.getImagePathForSinglePoint(
                feature.properties as DistributionPointProperties,
                'HP',
              ),
              'default',
            );
          case PointType.MultiHP:
            return this.setIcon(
              latLng,
              IconPaths.MULTIHP_UNKNOWN,
              'multi',
              true,
            );
          case PointType.MultiHC:
            return this.setIcon(
              latLng,
              IconPaths.MULTIHC_UNKNOWN,
              'multi',
              true,
            );
          case PointType.MultiHouseHold:
            return this.setIcon(
              latLng,
              IconPaths.MULTIHOUSEHOLD_UNKNOWN,
              'multi',
              true,
            );
          case PointType.MultiWell:
            return this.setIcon(
              latLng,
              IconPaths.MULTIWELL_UNKNOWN,
              'multi',
              true,
            );
          case PointType.CableWell:
          case PointType.SlingWell: {
            const iconPath = this.getImagePathForSinglePoint(
              feature.properties as DistributionPointProperties,
              'MANHOLE',
            );

            return this.setIcon(
              latLng,
              iconPath ===
                ('MANHOLE/DONE_WITH_DEVIATION' as unknown as IconPaths)
                ? IconPaths.MANHOLE_DONE
                : iconPath,
              'default',
            );
          }
          case PointType.DuctEndpoint:
            return this.setIcon(
              latLng,
              IconPaths.SDP_UNKNOWN as IconPaths,
              'sdp',
            );
          case PointType.DuctPoint:
            return this.setIcon(
              latLng,
              this.getImagePathForAggregatedPoint(
                feature.properties as DistributionPointProperties,
              ),
              'default',
            );
          default:
            return this.createCircleMarker(
              latLng,
              pointInfo[typeId].pointStyle,
              feature.properties as DistributionPointProperties,
            );
        }
      },
    });

    const popupType = pointInfo[typeId].popup;
    if (popupType) {
      layer.on('click', (e) => {
        if (popupType === PopupTypes.MultiHP) {
          this.handleClickMultiHP(e);
          return;
        }
        if (popupType === PopupTypes.MultiHC) {
          this.handleClickMultiHC(e);
          return;
        }
        if (popupType === PopupTypes.MultiHouseHold) {
          this.handleClickMultiHousehold(e);
          return;
        }
        if (popupType === PopupTypes.MultiWell) {
          this.handleClickMultiWell(e);
          return;
        }

        this.onClick(e, { popupType: popupType });
      });
    }
    this.layerData[typeId] = layer;
  }

  private clearLayerDataIfStoredIfsChanged() {
    const currentIfs = this.resourceService.getStoredResource()?.value;
    const storedIfs = this._storedIfs();
    if (storedIfs !== currentIfs && storedIfs != '') {
      this.layerData = {};
    }
    this._storedIfs.set(currentIfs ?? '');
  }

  private handleClickMultiWell(e: LeafletMouseEvent) {
    const key = e.latlng.lng.toString() + e.latlng.lat.toString();
    this.onClick(e, {
      popupType: PopupTypes.MultiWell,
      cluster: this.clusterPointService.markerClusterGroup[key].map(
        (marker) => ({
          ...marker,
          status: this.mapToCorrectReportStatus(marker.properties, ''),
        }),
      ),
    });
  }

  private handleClickMultiHP(e: LeafletMouseEvent) {
    const key = e.latlng.lng.toString() + e.latlng.lat.toString();
    this.onClick(e, {
      popupType: PopupTypes.MultiHP,
      cluster: this.clusterPointService.markerClusterGroup[key].map(
        (marker) => ({
          ...marker,
          status: this.mapToCorrectReportStatus(marker.properties, ''),
        }),
      ),
    });
  }

  private handleClickMultiHC(e: LeafletMouseEvent) {
    const key = e.latlng.lng.toString() + e.latlng.lat.toString();
    this.onClick(e, {
      popupType: PopupTypes.MultiHC,
      cluster: this.clusterPointService.markerClusterGroup[key].map(
        (marker) => ({
          ...marker,
        }),
      ),
    });
  }

  private handleClickMultiHousehold(e: LeafletMouseEvent) {
    const key = e.latlng.lng.toString() + e.latlng.lat.toString();
    this.onClick(e, {
      popupType: PopupTypes.MultiHouseHold,
      cluster: this.clusterPointService.markerClusterGroup[key].map(
        (marker) => ({
          ...marker,
        }),
      ),
    });
  }

  private createCircleMarker(
    latLng: LatLng,
    styles: PointStyles,
    properties: DistributionPointProperties,
  ) {
    const key = latLng.lng.toString() + latLng.lat.toString();

    const marker = circleMarker(latLng, {
      ...this.getPointColorByProgress(properties, styles.weight),
      fillColor: styles.fillColor,
      radius: styles.radius,
      fill: true,
      fillOpacity: 0.85,
    });

    return properties.markerTypeId === -1 &&
      this.clusterPointService.markerClusterGroup[key]
      ? marker.bindTooltip(
          this.clusterPointService.markerClusterGroup[key].length.toString(),
          {
            permanent: true,
            direction: 'center',
          },
        )
      : marker;
  }

  private setIcon(
    latLng: LatLng,
    path: IconPaths,
    sizeType: keyof typeof iconTypeSizes,
    multiPoint?: boolean,
  ) {
    const size: PointExpression = iconTypeSizes[sizeType] as PointExpression;
    if (multiPoint) {
      return this.setClusterDivIcon(latLng, path, size);
    }
    return this.setRegularIcon(latLng, path, size);
  }

  setRegularIcon(latLng: LatLng, path: IconPaths, size: PointExpression) {
    const icon = new Icon({
      iconUrl: `assets/leaflet/images/point-icons/${path}.svg`,
      iconAnchor: [16, 16],
      iconSize: size,
    });

    return new Marker(latLng, {
      icon,
    });
  }

  setClusterDivIcon(latLng: LatLng, path: IconPaths, size: PointExpression) {
    const key = latLng.lng.toString() + latLng.lat.toString();
    const clusterSize = this.clusterPointService.markerClusterGroup[key].length;
    const icon = new DivIcon({
      iconAnchor: [12, 12],
      iconSize: size,
      html: createClusterIconHtml(path, clusterSize, size),
    });

    return new Marker(latLng, {
      icon,
    });
  }

  private getImagePathForSinglePoint(
    properties: DistributionPointProperties,
    type: string,
  ) {
    const status = this.mapToCorrectReportStatus(properties, type);

    return `${type}/${status}` as IconPaths;
  }

  getImagePathForAggregatedPoint(
    properties: DistributionPointProperties,
  ): IconPaths {
    return this.kpReportStatusService.linkedHpReportsDone(properties)
      ? IconPaths.KP_DONE
      : IconPaths.KP_UNKNOWN;
  }

  mapToCorrectReportStatus = (
    properties: DistributionPointProperties,
    type: string,
  ): ReportStatus => {
    const matchedType = this.mapToMarkerType(type);
    if (matchedType) {
      const key = this.getKeyForType(type, properties);
      const existingReport = this.storedV3Reports()[matchedType]?.[key];
      if (existingReport) {
        return this.mapToCorrectIconBasedOnStatus(existingReport);
      }
    }
    return ReportStatus.Unknown;
  };

  getKeyForType(type: string, properties: DistributionPointProperties) {
    return type === 'HP' ? properties.sourceKey : properties.id;
  }

  mapToCorrectIconBasedOnStatus = (
    existingReport: RolloutProgressReportEntities.v3.ReportResponse,
  ): ReportStatus => {
    return mapStatusForIcon(existingReport.status) as ReportStatus;
  };

  mapToMarkerType = (
    type: string,
  ): RolloutProgressReportEntities.v3.MarkerTypes | undefined => {
    if (
      Object.values(RolloutProgressReportEntities.v3.MarkerTypes).includes(
        type as RolloutProgressReportEntities.v3.MarkerTypes,
      )
    ) {
      return type as RolloutProgressReportEntities.v3.MarkerTypes;
    }

    return undefined;
  };

  getPointColorByProgress(properties: DistributionPointProperties, weight = 1) {
    const status = this.mapToCorrectReportStatus(properties, '');
    return {
      color: reportStatusColors[status],
      dashArray: undefined,
      weight: status === ReportStatus.Unknown ? weight : 3,
    };
  }

  resetOnClick(e: LeafletMouseEvent, popupType: PopupTypes) {
    this.onClick(e, { popupType: popupType });
  }
}
